// client/src/apis/integrationApi.ts
import HttpClient from "./httpClient";
import { API_URLS } from "../utils/apiUrls";
import {
    TheoOverview,
    TheoOverviewFilterOptions,
    TimeSavedModel,
    AutoAgentFunnelEventsResponse,
    AutoAgentFunnelSummary,
    AutoAgentMonitorFilterOptions,
    AutoAgentMonitorResponse,
    AutoAgentMonitorSummary,
    AutoAgentMonitorSection,
    AutoAgentMonitorSortBy,
    AutoAgentMonitorSortDirection,
    Agent,
    AgentInput,
    AgentListResponse,
    AgentTicketsResponse,
    AutomationConfig,
    AutomationConfigListResponse,
    AutomationRulePreviewResponse,
    AutomationRulesConfig,
    AutomationVideoAvatarResponse,
    PlaybookWritePayload,
    TheoTaxonomyResponse,
} from "../types/integration";

// The M365 grant and test flows make a long, sequential chain of MS Graph API
// calls server-side (the grant issues ~80 app-role assignments; the test runs
// 15-25+ checks), each with a 120s server budget. The default 60s client
// timeout aborts these prematurely, so they get an extended timeout instead.
const M365_LONG_OPERATION_TIMEOUT_MS = 180000; // 3 minutes

/** The filters every Theo Overview request shares. */
export interface TheoOverviewQuery {
    window: string;
    agentId?: number;
    customerId?: string;
}

export type TheoOverviewSourceKind = "agent-tickets" | "assisted-tickets" | "conversations" | "time-saved";

function theoOverviewParams(params: TheoOverviewQuery): Record<string, string | number> {
    return {
        window: params.window,
        ...(params.agentId ? { agentId: params.agentId } : {}),
        ...(params.customerId ? { customerId: params.customerId } : {}),
    };
}

export interface RmmScriptComponentVariable {
    name: string;
    description?: string;
    required?: boolean;
    type?: string;
}

export interface RmmAutomation {
    uid: string;
    name: string;
    description?: string;
    type?: "ACTION" | "SCRIPT";
    operatingSystems?: string[];
}

export interface RmmScriptCatalogItem {
    slug: string;
    component_name: string;
    title: string;
    description: string;
    category: string;
    language: "powershell" | "shellscript";
    os: "windows" | "macos";
    os_family?: "windows" | "macos";
    default_timeout_sec: number;
    variables: RmmScriptComponentVariable[];
    file_name: string;
}

export type RmmWindowsScriptCatalogItem = RmmScriptCatalogItem;

export type DattoScriptComponentVariable = RmmScriptComponentVariable;
export type DattoWindowsScriptCatalogItem = RmmWindowsScriptCatalogItem;

export interface DocumentationLiveAvatarToken {
    sessionToken: string;
    sessionId: string;
    mode: "FULL";
    greetingText: string;
    maxSessionDurationSeconds: number;
}

class IntegrationApi extends HttpClient {
    protected url;

    constructor() {
        super();
        this.url = API_URLS;
    }


    public getCustomers = () => 
        this.get('/customers');

    public getMspOrgCustomer = () =>
        this.get('/customers/msp-org');

    public updateMspOrgIdentitySettings = (data: {
        auto_mode_notify_enabled?: boolean;
        default_notify_upn?: string;
        default_from_mailbox?: string;
        teams_chat_id?: string;
        teams_team_id?: string;
        teams_channel_id?: string;
    }) => this.put('/customers/msp-org/identity-settings', {}, data);

    public getMspOrgDelegatedSender = () =>
        this.get('/customers/msp-org/delegated-sender');

    public startMspOrgDelegatedSender = (redirectUri: string) =>
        this.post('/customers/msp-org/delegated-sender/start', {}, { redirect_uri: redirectUri });

    public completeMspOrgDelegatedSender = (data: { code: string; redirect_uri: string }) =>
        this.post('/customers/msp-org/delegated-sender', {}, data);

    public disconnectMspOrgDelegatedSender = () =>
        this.del('/customers/msp-org/delegated-sender');

    public getTeamsBotCredentials = () =>
        this.get('/api/teams-bot/credentials');

    public saveTeamsBotCredentials = (data: { app_id: string; app_tenant_id: string; secret_value: string }) =>
        this.put('/api/teams-bot/credentials', {}, data);

    public deleteTeamsBotCredentials = () =>
        this.del('/api/teams-bot/credentials');

    public testTeamsBotConnection = () =>
        this.post('/api/teams-bot/test', {}, {});

    public grantTeamsBotPermissions = () =>
        this.post('/api/teams-bot/permissions/grant', {}, {});

    public startTeamsBotAdminConsent = () =>
        this.post('/api/teams-bot/permissions/consent/start', {}, {});

    public pollTeamsBotAdminConsent = (deviceCode: string) =>
        this.post('/api/teams-bot/permissions/consent/poll', {}, { device_code: deviceCode });

    public downloadTeamsBotPackage = () =>
        this.getBlob('/api/teams-bot/package');

    public getTeamsBotInstallLink = () =>
        this.get('/api/teams-bot/install-link');

    public saveTeamsCatalogAppId = (catalogAppId: string) =>
        this.put('/api/teams-bot/catalog-app', {}, { catalog_app_id: catalogAppId });

    public saveTeamsAssigneeNotifications = (enabled: boolean) =>
        this.put('/api/teams-bot/notifications', {}, { notify_assignee_change: enabled });

    public lookupTeamsCatalogApp = () =>
        this.post('/api/teams-bot/install-link/lookup', {}, {});

    public getTeamsBotTeams = () =>
        this.get('/api/teams-bot/teams');

    public getTeamsBotChannels = (teamId: string) =>
        this.get(`/api/teams-bot/teams/${encodeURIComponent(teamId)}/channels`);

    public getMspOrgTeams = () =>
        this.get('/customers/msp-org/teams');

    public getMspOrgTeamChannels = (teamId: string) =>
        this.get(`/customers/msp-org/teams/${encodeURIComponent(teamId)}/channels`);

    public getMspOrgChats = () =>
        this.get('/customers/msp-org/chats');

    public getCustomerById = (customerId: string) => 
        this.get(`/customers/${customerId}`);

    public createCustomer = (data: any) => 
        this.post('/customers', {}, data);

    public syncPsaCustomers = (): Promise<{ imported: number; updated: number; skipped: number }> =>
        this.post('/customers/sync', { timeout: 180000 }, {});

    public updateCustomer = (customerId: string, data: any) => 
        this.put(`/customers/${customerId}`, {}, data);

    public deleteCustomer = (customerId: string) => 
        this.del(`/customers/${customerId}`);

    public getAutomationConfigsByMspId = (mspId: string | number): Promise<AutomationConfigListResponse | AutomationConfig[]> =>
        this.get(`/automation-config/msp/${mspId}`);

    public getTheoTicketTaxonomy = (): Promise<TheoTaxonomyResponse> =>
        this.get("/automation-config/theo-taxonomy");

    public getAutomationVideoAvatar = (): Promise<AutomationVideoAvatarResponse> =>
        this.get("/automation-config/video-avatar");

    public refreshAutomationVideoAvatar = (): Promise<AutomationVideoAvatarResponse> =>
        this.post("/automation-config/video-avatar/refresh", {}, {});

    public createDocumentationLiveAvatarToken = (): Promise<DocumentationLiveAvatarToken> =>
        this.post("/help-center/documentation/live-avatar/session-token", {}, {});

    private saveAutomationConfigPayload = (
        path: string,
        data: {
            automation_capacity: number;
            suggestions_enabled?: boolean;
            auto_resolver_enabled?: boolean;
            auto_resolver_ticket_type_allowlist?: string;
            rules_json?: AutomationRulesConfig | null;
            customer_interaction: number;
        }
    ): Promise<AutomationConfig> => this.put(path, {}, data);

    public saveAutomationConfig = (
        customerId: string | number,
        data: {
            automation_capacity: number;
            suggestions_enabled?: boolean;
            auto_resolver_enabled?: boolean;
            auto_resolver_ticket_type_allowlist?: string;
            rules_json?: AutomationRulesConfig | null;
            customer_interaction: number;
        }
    ): Promise<AutomationConfig> =>
        this.saveAutomationConfigPayload(`/automation-config/customers/${customerId}`, data);

    public saveMspAutomationDefault = (
        mspId: string | number,
        data: {
            automation_capacity: number;
            suggestions_enabled?: boolean;
            auto_resolver_enabled?: boolean;
            auto_resolver_ticket_type_allowlist?: string;
            rules_json?: AutomationRulesConfig | null;
            customer_interaction: number;
        }
    ): Promise<AutomationConfig> =>
        this.saveAutomationConfigPayload(`/automation-config/msp/${mspId}/default`, data);

    public deleteAutomationConfig = (customerId: string | number): Promise<{ success: boolean; deleted: boolean }> =>
        this.del(`/automation-config/customers/${customerId}`);

    // Agents. The MSP is taken from the caller's own token server-side, so none of these take one.
    public getAgents = (): Promise<AgentListResponse> => this.get("/agents");

    public getAgent = (agentId: number | number): Promise<Agent> => this.get(`/agents/${agentId}`);

    /** Every ticket this agent has worked, newest activity first. */
    public getAgentTickets = (agentId: number | number, limit?: number): Promise<AgentTicketsResponse> =>
        this.get(`/agents/${agentId}/tickets${limit ? `?limit=${limit}` : ""}`);

    public createAgent = (data: AgentInput): Promise<Agent> => this.post("/agents", {}, data);

    /** Only the keys present are changed. `slug` and `is_default` are not editable. */
    public updateAgent = (agentId: number | number, data: AgentInput): Promise<Agent> =>
        this.put(`/agents/${agentId}`, {}, data);

    /** Rejected with 409 for the default agent, which can be disabled but not removed. */
    public deleteAgent = (agentId: number | number): Promise<{ deleted: boolean; slug: string }> =>
        this.del(`/agents/${agentId}`);


    public previewAutomationRuleMatches = (
        customerId: string | number,
        data: {
            rules_json: AutomationRulesConfig;
            scopes: Array<{
                scope_type: "all" | "queue" | "category";
                queue_id: number;
                scope_name?: string;
            }>;
            max_matches?: number;
        }
    ): Promise<AutomationRulePreviewResponse> =>
        this.post(`/automation-config/customers/${customerId}/rule-preview`, { timeout: 120000 }, data);

    public getAutomationPsaAssignees = () =>
        this.get("/automation-config/psa/assignees");

    public getAutomationPsaStatuses = () =>
        this.get("/automation-config/psa/statuses");

    public getAutomationPsaPriorities = () =>
        this.get("/automation-config/psa/priorities");

    public getAutoAgentMonitor = (
        params: {
            section: AutoAgentMonitorSection;
            page: number;
            pageSize: number;
            refreshStatuses?: boolean;
            sortBy?: AutoAgentMonitorSortBy;
            sortDirection?: AutoAgentMonitorSortDirection;
            ticketId?: string;
            customerKeys?: string[];
            ticketStatuses?: string[];
            statusCategories?: string[];
            ticketTypes?: string[];
            executionStatuses?: string[];
            startedAfter?: string;
            startedBefore?: string;
            agentId?: number;
        }
    ): Promise<AutoAgentMonitorResponse> =>
        this.get("/auto-agent-monitor", {}, {
            section: params.section,
            page: String(params.page),
            pageSize: String(params.pageSize),
            refreshStatuses: String(params.refreshStatuses ?? true),
            sortBy: params.sortBy || "last_updated",
            sortDirection: params.sortDirection || "desc",
            ...(params.agentId ? { agentId: params.agentId } : {}),
            ...(params.ticketId ? { ticketId: params.ticketId } : {}),
            ...(params.customerKeys?.length ? { customerKeys: params.customerKeys } : {}),
            ...(params.ticketStatuses?.length ? { ticketStatuses: params.ticketStatuses } : {}),
            ...(params.statusCategories?.length ? { statusCategories: params.statusCategories } : {}),
            ...(params.ticketTypes?.length ? { ticketTypes: params.ticketTypes } : {}),
            ...(params.executionStatuses?.length ? { executionStatuses: params.executionStatuses } : {}),
            ...(params.startedAfter ? { startedAfter: params.startedAfter } : {}),
            ...(params.startedBefore ? { startedBefore: params.startedBefore } : {}),
        });

    public getAutoAgentMonitorSummary = (window = "all", agentId?: number): Promise<AutoAgentMonitorSummary> =>
        this.get("/auto-agent-monitor/summary", {}, {
            window,
            ...(agentId ? { agentId } : {}),
        });

    public getTheoOverview = (params: TheoOverviewQuery): Promise<TheoOverview> =>
        this.get("/theo-overview", {}, theoOverviewParams(params));

    /** The records behind one overview figure, fetched when its list is opened. */
    public getTheoOverviewSources = <T>(source: TheoOverviewSourceKind, params: TheoOverviewQuery): Promise<T> =>
        this.get(`/theo-overview/sources/${source}`, {}, theoOverviewParams(params));

    public getTheoOverviewFilterOptions = (): Promise<TheoOverviewFilterOptions> =>
        this.get("/theo-overview/filter-options", {}, {});

    public getTimeSavedModel = (): Promise<TimeSavedModel> => this.get("/theo-overview/time-saved-model", {}, {});

    public getAutoAgentMonitorFilterOptions = (agentId?: number): Promise<AutoAgentMonitorFilterOptions> =>
        this.get("/auto-agent-monitor/filter-options", {}, agentId ? { agentId } : {});

    public getAutoAgentFunnelSummary = (window = "48h"): Promise<AutoAgentFunnelSummary> =>
        this.get("/auto-agent-monitor/funnel-summary", {}, { window });

    public getAutoAgentFunnelEvents = (params: {
        window?: string;
        stage?: string;
        ticketId?: string;
        customer?: string;
        page: number;
        pageSize: number;
    }): Promise<AutoAgentFunnelEventsResponse> =>
        this.get("/auto-agent-monitor/funnel-events", {}, {
            window: params.window || "48h",
            ...(params.stage ? { stage: params.stage } : {}),
            ...(params.ticketId ? { ticketId: params.ticketId } : {}),
            ...(params.customer ? { customer: params.customer } : {}),
            page: String(params.page),
            pageSize: String(params.pageSize),
        });
    
    // Application Routes
    public getApplicationsByCustomer = (customerId: string) =>
        this.get(`/customers/${customerId}/applications`);

    public getCustomerProductivityAppsStatus = (customerId: string) =>
        this.get(`/customers/${customerId}/productivity-apps-status`);

    // Searches the customer's connected Microsoft 365 or Google Workspace directory.
    public searchCustomerDirectoryUsers = (customerId: string, query: string) =>
        this.get(`/customers/${customerId}/directory-users`, {}, { q: query });

    // Ticket authorization approvers, shared by every agent. Each agent's policy saves with the agent.
    public getCustomerApproverSummary = () => this.get('/ticket-authorization/approver-summary');

    public getCustomerAuthorizationApprovers = (customerId: string) =>
        this.get(`/customers/${customerId}/authorization-approvers`);

    public saveCustomerAuthorizationApprovers = (customerId: string, data: object) =>
        this.put(`/customers/${customerId}/authorization-approvers`, {}, data);

    public getApplicationPlaybooks = (mspId: string | number) =>
        this.get(`/playbooks/msp/${mspId}`);

    public getApplicationSkills = (mspId: string | number) =>
        this.getApplicationPlaybooks(mspId);

    public normalizePlaybook = (playbook: {
        name?: string;
        description?: string;
        skill_text: string;
        entity_type?: string;
        entity_name?: string;
    }): Promise<{ name: string; description: string; skill_text: string }> =>
        this.post('/playbooks/normalize', { timeout: 120000 }, playbook);

    public createGeneralPlaybook = (skill: PlaybookWritePayload) =>
        this.post('/playbooks/general', {}, skill);

    public getCustomerPlaybooks = (customerId: string | number) =>
        this.get(`/playbooks/customer/${customerId}`);

    public createCustomerPlaybook = (
        customerId: string | number,
        skill: PlaybookWritePayload
    ) => this.post(`/playbooks/customer/${customerId}`, {}, skill);

    public createApplicationPlaybook = (
        applicationId: string | number,
        mspId: string | number,
        skill: PlaybookWritePayload
    ) => this.post(
        `/playbooks/application/${applicationId}`,
        {},
        { msp_id: mspId, ...skill }
    );

    public createApplicationSkill = (
        applicationId: string | number,
        mspId: string | number,
        skill: PlaybookWritePayload
    ) => this.createApplicationPlaybook(applicationId, mspId, skill);

    public createPlatformPlaybook = (
        platformId: string | number,
        mspId: string | number,
        skill: PlaybookWritePayload
    ) => this.post(
        `/playbooks/platform/${platformId}`,
        {},
        { msp_id: mspId, ...skill }
    );

    public createPlatformSkill = (
        platformId: string | number,
        mspId: string | number,
        skill: PlaybookWritePayload
    ) => this.createPlatformPlaybook(platformId, mspId, skill);

    public updatePlaybook = (
        skillId: string | number,
        skill: PlaybookWritePayload
    ) => this.put(`/playbooks/${skillId}`, {}, skill);

    public updateSkill = (
        skillId: string | number,
        skill: PlaybookWritePayload
    ) => this.updatePlaybook(skillId, skill);

    public deletePlaybook = (skillId: string | number) =>
        this.del(`/playbooks/${skillId}`);

    public deleteSkill = (skillId: string | number) =>
        this.deletePlaybook(skillId);

    public saveApplicationConfigPlaybook = (
        applicationConfigId: string | number,
        mspId: string | number,
        skillText: string
    ) => this.put(
        `/playbooks/application-config/${applicationConfigId}`,
        {},
        { msp_id: mspId, skill_text: skillText }
    );

    public saveApplicationConfigSkill = (
        applicationConfigId: string | number,
        mspId: string | number,
        skillText: string
    ) => this.saveApplicationConfigPlaybook(applicationConfigId, mspId, skillText);

    public saveApplicationPlaybook = (
        applicationId: string | number,
        mspId: string | number,
        skillText: string
    ) => this.put(
        `/playbooks/application/${applicationId}`,
        {},
        { msp_id: mspId, skill_text: skillText }
    );

    public saveApplicationSkill = (
        applicationId: string | number,
        mspId: string | number,
        skillText: string
    ) => this.saveApplicationPlaybook(applicationId, mspId, skillText);

    public deleteApplicationConfigPlaybook = (
        applicationConfigId: string | number,
        mspId: string | number
    ) => this.del(
        `/playbooks/application-config/${applicationConfigId}`,
        {},
        { msp_id: String(mspId) }
    );

    public deleteApplicationConfigSkill = (
        applicationConfigId: string | number,
        mspId: string | number
    ) => this.deleteApplicationConfigPlaybook(applicationConfigId, mspId);

    public deleteApplicationPlaybook = (
        applicationId: string | number,
        mspId: string | number
    ) => this.del(
        `/playbooks/application/${applicationId}`,
        {},
        { msp_id: String(mspId) }
    );

    public deleteApplicationSkill = (
        applicationId: string | number,
        mspId: string | number
    ) => this.deleteApplicationPlaybook(applicationId, mspId);

    public savePlatformPlaybook = (
        platformId: string | number,
        mspId: string | number,
        skillText: string
    ) => this.put(
        `/playbooks/platform/${platformId}`,
        {},
        { msp_id: mspId, skill_text: skillText }
    );

    public savePlatformSkill = (
        platformId: string | number,
        mspId: string | number,
        skillText: string
    ) => this.savePlatformPlaybook(platformId, mspId, skillText);

    public deletePlatformPlaybook = (
        platformId: string | number,
        mspId: string | number
    ) => this.del(
        `/playbooks/platform/${platformId}`,
        {},
        { msp_id: String(mspId) }
    );

    public deletePlatformSkill = (
        platformId: string | number,
        mspId: string | number
    ) => this.deletePlatformPlaybook(platformId, mspId);

    // Platform ingestion status/trigger (optional platform_id for psa_tickets — Horizon platform row)
    getIngestionStatus = (
      sourceDocType: "guide" | "kb" | "hudu_articles" | "itglue_docs" | "psa_tickets",
      platformId?: number,
      mspId?: number
    ) =>
      this.get(
        "/reindex/status",
        {},
        {
          source_doc_type: sourceDocType,
          ...(mspId != null ? { mspId } : {}),
          ...(platformId != null ? { platform_id: platformId } : {}),
        }
      );

    triggerIngestion = (
      sourceDocType: "guide" | "kb" | "hudu_articles" | "itglue_docs" | "psa_tickets",
      platformId?: number,
      mspId?: number
    ) =>
      this.post(
        "/reindex/trigger",
        {},
        {
          source_doc_type: sourceDocType,
          ...(mspId != null ? { mspId } : {}),
          ...(platformId != null ? { platform_id: platformId } : {}),
        }
      );

    public getApplicationDetails = (applicationId: string) => 
        this.get(`/applications/${applicationId}`);

    public getApplicationPermissions = (applicationId: string) => 
        this.get(`/applications/${applicationId}/permissions`);

    // Action Routes
    public getApplicationActions = (customerId: string, applicationId: string) => 
        this.get(`/customers/${customerId}/applications/${applicationId}/actions`, {}, { _ts: Date.now() });

    public updateActionPermission = (
        customerId: string, 
        applicationId: string, 
        actionName: string, 
        permission: string
    ) => this.patch(
        `/customers/${customerId}/applications/${applicationId}/actions/${actionName}/permission`,
        {},
        { permission }
    );

    public updateActionConfig = (
        customerId: string, 
        applicationId: string, 
        actionName: string, 
        config: object
    ) => this.patch(
        `/customers/${customerId}/applications/${applicationId}/actions/${actionName}/config`,
        {},
        { config }
    );

    // Application State Management
    public updateApplicationState = (
        customerId: string, 
        applicationId: string, 
        enabled: boolean
    ) => this.patch(
        `/customers/${customerId}/applications/${applicationId}/state`,
        {},
        { enabled }
    );

    // Application Configuration Management
    public updateApplicationConfig = (
        customerId: string, 
        applicationId: string, 
        config: object
    ) => {
        console.log("[IntegrationApi] updateApplicationConfig called", {
            url: `/customers/${customerId}/applications/${applicationId}/config`,
            configKeys: config ? Object.keys(config) : [],
        });
        return this.put(
            `/customers/${customerId}/applications/${applicationId}/config`,
            {},
            { config }
        );
    };

    // Customer Application Management
    public addApplicationToCustomer = (
        customerId: string, 
        applicationId: string, 
        settings: object
    ) => this.post(
        `/customers/${customerId}/applications/${applicationId}`,
        {},
        { settings }
    );

    public removeApplicationFromCustomer = (
        customerId: string, 
        applicationId: string
    ) => this.del(
        `/customers/${customerId}/applications/${applicationId}`
    );

    public addApplicationToCustomerByName = (
        customerId: string,
        applicationName: string,
        config: Record<string, unknown>
    ) => this.post(
        `/customers/${customerId}/applications/add`,
        {},
        { application_name: applicationName, config }
    );

    public testAndEnableM365Actions = (customerId: string) =>
        this.post(
            `/customers/${customerId}/applications/m365/test-and-enable-actions`,
            { timeout: M365_LONG_OPERATION_TIMEOUT_MS },
            {}
        );

    // Platform Routes
    public getPlatforms = (msp_id?: number | string) => {
        const params = msp_id ? { msp_id: String(msp_id) } : {};
        return this.get('/platforms', {}, params);
    }

    public getPlatformActions = (platformId: string) => 
        this.get(`/platforms/${platformId}/actions`);

    public getPlatformConfig = (platformId: string) => 
        this.get(`/platforms/${platformId}/config`);

    public listDattoScriptComponents = (platformId: string): Promise<{ components: RmmAutomation[] }> =>
        this.get(`/platforms/${platformId}/datto/script-components`);

    public listNinjaOneAutomations = (platformId: string): Promise<{ automations: RmmAutomation[] }> =>
        this.get(`/platforms/${platformId}/ninjaone/automations`);

    public getDattoWindowsScriptCatalog = (): Promise<{ scripts: RmmWindowsScriptCatalogItem[] }> =>
        this.get('/datto/artifacts/windows-script-catalog');

    public getNinjaOneWindowsScriptCatalog = (): Promise<{ scripts: RmmWindowsScriptCatalogItem[] }> =>
        this.get('/ninjaone/artifacts/windows-script-catalog');

    public getDattoMacosScriptCatalog = (): Promise<{ scripts: RmmScriptCatalogItem[] }> =>
        this.get('/datto/artifacts/macos-script-catalog');

    public getNinjaOneMacosScriptCatalog = (): Promise<{ scripts: RmmScriptCatalogItem[] }> =>
        this.get('/ninjaone/artifacts/macos-script-catalog');

    public downloadDattoWindowsScriptComponents = (scriptSlugs?: string[]) =>
        this.getBlob(
            '/datto/artifacts/windows-script-components',
            scriptSlugs?.length ? { slugs: scriptSlugs.join(',') } : undefined
        );

    public downloadNinjaOneWindowsScripts = (scriptSlugs?: string[]) =>
        this.getBlob(
            '/ninjaone/artifacts/windows-scripts',
            scriptSlugs?.length ? { slugs: scriptSlugs.join(',') } : undefined
        );

    public downloadDattoMacosScriptComponents = (scriptSlugs?: string[]) =>
        this.getBlob(
            '/datto/artifacts/macos-script-components',
            scriptSlugs?.length ? { slugs: scriptSlugs.join(',') } : undefined
        );

    public downloadNinjaOneMacosScripts = (scriptSlugs?: string[]) =>
        this.getBlob(
            '/ninjaone/artifacts/macos-scripts',
            scriptSlugs?.length ? { slugs: scriptSlugs.join(',') } : undefined
        );

    public downloadDattoWindowsShellSessionComponent = () =>
        this.getBlob('/datto/artifacts/windows-shell-session-component');

    public downloadDattoMacosShellSessionComponent = () =>
        this.getBlob('/datto/artifacts/macos-shell-session-component');

    public downloadNinjaOneWindowsShellSessionScript = () =>
        this.getBlob('/ninjaone/artifacts/windows-shell-session-script');

    public downloadNinjaOneMacosShellSessionScript = () =>
        this.getBlob('/ninjaone/artifacts/macos-shell-session-script');

    public getDocumentationRmmCapabilities = () =>
        this.get('/help-center/documentation/rmm-capabilities');

    public updatePlatformState = (
        platformId: string,
        enabled: boolean
    ) => this.put(
        `/platforms/${platformId}/state`,
        {},
        { enabled }
    );

    public updatePlatformActionPermission = (
        platformId: string, 
        actionId: string, 
        permission: string
    ) => this.patch(
        `/platforms/${platformId}/actions/${actionId}/permission`,
        {},
        { permission }
    );

    public updatePlatformConfig = (
        platformId: string,
        config: object
    ) => this.put(
        `/platforms/${platformId}/config`,
        {},
        { config }
    );

    // Statistics and Audit
    public getApplicationStats = (customerId: string) => 
        this.get(`/customers/${customerId}/stats`);

    public getPlatformIntegrationStatus = (customerId: string) => 
        this.get(`/customers/${customerId}/platform-integration`);

    public getActionAuditLog = (params: {
        customerId: string;
        applicationId: string;
        actionName: string;
        startDate: string;
        endDate: string;
    }) => this.get('/audit-logs', {}, params);

    // Test Application Configuration
    public testApplicationConfiguration = (
        customerId: string,
        appName: string,
        config: Record<string, unknown>
    ) => this.post(
        `/customers/${customerId}/applications/${appName}/test`,
        { timeout: M365_LONG_OPERATION_TIMEOUT_MS },
        { config }
    );

    public getM365ClientSecretExpiry = (customerId: string) =>
        this.get(`/customers/${customerId}/applications/m365/client-secret-expiry`);

    // Test Platform Configuration
    public testPlatformConfiguration = (
        _customerId: string,
        platformName: string,
        config: Record<string, string>
    ) => this.post(
        `/platforms/${platformName}/test`,
        {},
        { config }
    );

    public getMsps = () => 
        this.get('/msps');

    public getMspById = (mspId: string) => 
        this.get(`/msps/${mspId}`);

    public getCustomersByMspId = (mspId: string) => 
        this.get(`/customers/msp/${mspId}`);

    // User Management
    public getUsersByMspId = (mspId: string) => 
        this.get(`/users/msp/${mspId}`, {}, { _ts: String(Date.now()) });

    public getPsaUsersByMspId = (mspId: string) =>
        this.get(`/users/msp/${mspId}/psa-users`, {}, { _ts: String(Date.now()) });

    public createUser = (data: any) => 
        this.post('/users', {}, data);

    public updateUser = (userId: string, data: any) => 
        this.put(`/users/${userId}`, {}, data);

    public deleteUser = (userId: string) => 
        this.del(`/users/${userId}`);

    // Platform Creation
    public createPlatform = (mspId: string | number, data: any) => 
        this.post('/platforms', {}, { ...data, msp_id: mspId });

    // Application Creation
    public createApplication = (data: any) => 
        this.post('/applications', {}, data);

    // Get Available Platforms
    public getAvailablePlatforms = () => 
        this.get('/platforms/available');

    // Get Available Applications
    public getAvailableApplications = () => 
        this.get('/applications/available');

    // Test Platform Configuration (without customerId - for testing during creation)
    public testPlatformConfigurationDirect = (
        platformName: string,
        config: Record<string, string>
    ) => this.post(
        `/platforms/${platformName}/test`,
        {},
        { config }
    );

    public exchangeNinjaOneAuthorizationCode = (payload: {
        instance_url: string;
        client_id: string;
        client_secret: string;
        code: string;
        redirect_uri: string;
    }): Promise<{ refresh_token: string; scope?: string; expires_in?: number }> =>
        this.post('/platforms/ninjaone/oauth/exchange', {}, payload);

    // Test Application Configuration (without customerId - for testing during creation)
    public testApplicationConfigurationDirect = (
        applicationName: string,
        config: Record<string, unknown>,
        opts?: { credentialsOnly?: boolean }
    ) => this.post(
        `/applications/${applicationName}/test`,
        {},
        {
            config,
            ...(opts?.credentialsOnly ? { credentialsOnly: true } : {}),
        }
    );
}

export default IntegrationApi;
export const integrationApiInstance = new IntegrationApi();
